import React from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  Stack,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { Copy } from 'lucide-react';
import { getCompanyName, QuoteItem } from '../../../utils/types';
import { QuoteField, QuoteSectionCard, QuoteStatusBadge, formatQuoteDate, formatQuoteMoney } from './quoteUi';

export const generateQuoteReference = () =>
  `Q-${new Date().toISOString().slice(2, 10).replace(/-/g, '')}-${Math.random()
    .toString(36)
    .substring(2, 6)
    .toUpperCase()}`;

export const calculateQuoteTotals = (items: QuoteItem[]) => {
  const subtotal = items.reduce((sum, item) => sum + Number(item.total_price || 0), 0);
  const total = subtotal;
  return { subtotal, total };
};

type RecipientSummaryProps = {
  selectedLeadId?: string;
  selectedLead?: any;
  selector: React.ReactNode;
};

export const QuoteRecipientSection = ({
  selectedLead,
  selectedLeadId,
  selector,
}: RecipientSummaryProps) => (
  <QuoteSectionCard
    title="Recipient"
    description="Confirm who this quotation is for before you build pricing."
    action={selectedLeadId ? <QuoteStatusBadge status="draft" /> : undefined}
  >
    <Stack spacing={4}>
      {selector}
      {selectedLead ? (
        <HStack
          justify="space-between"
          align={{ base: 'flex-start', md: 'center' }}
          flexWrap="wrap"
          p={4}
          bg="gray.50"
          borderWidth="1px"
          borderColor="gray.100"
          borderRadius="18px"
        >
          <VStack align="flex-start" spacing={1}>
            <Text fontWeight="800" color="gray.900">
              {`${selectedLead.title ? `${selectedLead.title} ` : ''}${selectedLead.first_name || ''} ${selectedLead.second_name || ''}`.trim() || 'Unnamed lead'}
            </Text>
            <Text fontSize="sm" color="gray.500">
              {getCompanyName(selectedLead.company) || 'No company linked'}
            </Text>
          </VStack>
          <VStack align={{ base: 'flex-start', md: 'flex-end' }} spacing={1}>
            <Text fontSize="sm" color="gray.600">
              {selectedLead.email || 'No email'}
            </Text>
            <Text fontSize="sm" color="gray.600">
              {selectedLead.phone || 'No phone'}
            </Text>
          </VStack>
        </HStack>
      ) : (
        <Text fontSize="sm" color="gray.500">
          Select a lead to attach the quotation to the correct customer and company context.
        </Text>
      )}
    </Stack>
  </QuoteSectionCard>
);

type LineItemsProps = {
  items: QuoteItem[];
  products: Array<{ id: string; name: string; price?: number }>;
  onAddItem: () => void;
  onDuplicateItem?: (index: number) => void;
  onChangeItem: (index: number, field: keyof QuoteItem, value: string | number) => void;
  onRemoveItem: (index: number) => void;
};

export const QuoteLineItemsSection = ({
  items,
  products,
  onAddItem,
  onDuplicateItem,
  onChangeItem,
  onRemoveItem,
}: LineItemsProps) => (
  <QuoteSectionCard
    title="Line items"
    description="Add products or services and let totals update instantly as you build."
    action={
      <Button leftIcon={<AddIcon />} borderRadius="14px" onClick={onAddItem}>
        Add line
      </Button>
    }
  >
    <Stack spacing={4}>
      {items.length === 0 ? (
        <VStack
          py={10}
          px={6}
          borderRadius="20px"
          borderWidth="1px"
          borderColor="gray.100"
          bg="gray.50"
          spacing={3}
          textAlign="center"
        >
          <Text fontWeight="800" color="gray.900">
            No line items yet
          </Text>
          <Text fontSize="sm" color="gray.500" maxW="md">
            Start with the core products or services. Quantity, price, and totals will calculate live as you go.
          </Text>
          <Button leftIcon={<AddIcon />} borderRadius="14px" onClick={onAddItem}>
            Add first line
          </Button>
        </VStack>
      ) : (
        items.map((item, index) => (
          <Box
            key={item.id}
            borderWidth="1px"
            borderColor="gray.100"
            borderRadius="20px"
            p={{ base: 4, md: 5 }}
            bg="gray.50"
          >
            <Stack spacing={4}>
              <HStack justify="space-between" align="center">
                <Text fontWeight="700" color="gray.900">
                  Line {index + 1}
                </Text>
                <HStack spacing={1}>
                  {onDuplicateItem ? (
                    <IconButton
                      aria-label="Duplicate line"
                      icon={<Copy size={16} />}
                      variant="ghost"
                      size="sm"
                      onClick={() => onDuplicateItem(index)}
                    />
                  ) : null}
                  <IconButton
                    aria-label="Delete line"
                    icon={<DeleteIcon />}
                    variant="ghost"
                    colorScheme="red"
                    size="sm"
                    onClick={() => onRemoveItem(index)}
                  />
                </HStack>
              </HStack>

              <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
                <FormControl flex={2}>
                  <FormLabel>Product or service</FormLabel>
                  <Select
                    placeholder="Select item"
                    value={item.item_name}
                    onChange={(event) => onChangeItem(index, 'item_name', event.target.value)}
                    bg="white"
                    borderRadius="16px"
                  >
                    {products.map((product) => (
                      <option key={product.id} value={product.name}>
                        {product.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl flex={1}>
                  <FormLabel>Quantity</FormLabel>
                  <NumberInput
                    min={1}
                    value={item.quantity}
                    onChange={(valueString, valueNumber) =>
                      onChangeItem(index, 'quantity', Number.isNaN(valueNumber) ? 1 : valueNumber)
                    }
                  >
                    <NumberInputField bg="white" borderRadius="16px" />
                  </NumberInput>
                </FormControl>

                <FormControl flex={1}>
                  <FormLabel>Unit price</FormLabel>
                  <NumberInput
                    min={0}
                    precision={2}
                    value={item.unit_price}
                    onChange={(valueString, valueNumber) =>
                      onChangeItem(index, 'unit_price', Number.isNaN(valueNumber) ? 0 : valueNumber)
                    }
                  >
                    <NumberInputField bg="white" borderRadius="16px" />
                  </NumberInput>
                </FormControl>

                <FormControl flex={1}>
                  <FormLabel>Line total</FormLabel>
                  <Input
                    isReadOnly
                    value={formatQuoteMoney(item.total_price)}
                    bg="white"
                    borderRadius="16px"
                    fontWeight="700"
                  />
                </FormControl>
              </Stack>
            </Stack>
          </Box>
        ))
      )}
    </Stack>
  </QuoteSectionCard>
);

export const QuoteNotesSection = ({
  notes,
  validUntil,
  onChangeNotes,
  onChangeValidUntil,
}: {
  notes: string;
  validUntil?: string | null;
  onChangeNotes: (value: string) => void;
  onChangeValidUntil: (value: string) => void;
}) => (
  <QuoteSectionCard
    title="Notes and validity"
    description="Keep customer-facing context clear and set an expiry when needed."
  >
    <Stack spacing={5}>
      <FormControl>
        <FormLabel>Quote validity date</FormLabel>
        <Input
          type="date"
          value={validUntil || ''}
          onChange={(event) => onChangeValidUntil(event.target.value)}
          borderRadius="16px"
          bg="gray.50"
        />
      </FormControl>
      <FormControl>
        <FormLabel>Notes</FormLabel>
        <Textarea
          value={notes}
          onChange={(event) => onChangeNotes(event.target.value)}
          minH="160px"
          borderRadius="16px"
          bg="gray.50"
          placeholder="Add customer-facing notes, delivery context, or commercial instructions."
        />
      </FormControl>
    </Stack>
  </QuoteSectionCard>
);

export const QuoteSummaryRail = ({
  referenceNumber,
  status,
  items,
  notes,
  validUntil,
}: {
  referenceNumber: string;
  status?: string;
  items: QuoteItem[];
  notes: string;
  validUntil?: string | null;
}) => {
  const totals = calculateQuoteTotals(items);

  return (
    <Stack spacing={6}>
      <QuoteSectionCard
        title="Commercial summary"
        description="Keep the total, status, and readiness visible while you build."
      >
        <Stack spacing={4}>
          <QuoteField label="Quote reference" value={referenceNumber || 'Pending'} />
          <QuoteField label="Status" value={<QuoteStatusBadge status={status || 'draft'} />} />
          <QuoteField label="Line items" value={`${items.length} line${items.length === 1 ? '' : 's'}`} />
          <QuoteField label="Validity" value={validUntil ? formatQuoteDate(validUntil) : 'No expiry set'} />
          <Box pt={2}>
            <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.08em" color="gray.500" mb={1}>
              Total
            </Text>
            <Text fontSize="3xl" fontWeight="800" color="gray.900">
              {formatQuoteMoney(totals.total)}
            </Text>
          </Box>
        </Stack>
      </QuoteSectionCard>

      <QuoteSectionCard
        title="Readiness"
        description="A quick check before the quote is saved, shared, or sent."
      >
        <Stack spacing={4}>
          <QuoteField
            label="Pricing"
            value={items.length ? 'Ready' : 'Add at least one line item'}
          />
          <QuoteField
            label="Notes"
            value={notes?.trim() ? 'Included' : 'No notes added'}
          />
          <QuoteField
            label="Expiry"
            value={validUntil ? 'Defined' : 'Missing validity date'}
          />
        </Stack>
      </QuoteSectionCard>
    </Stack>
  );
};
